import { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import decimalToFraction from "../utils/decimalToFraction";
import { formatIngredientWithMetric } from "../utils/unitConversion";

/**
 * Custom hook for generating PDFs from recipes
 * Provides both basic PDF generation and advanced HTML-to-PDF conversion
 */
export const usePDFGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Render a single recipe layout to PDF
   * @param {Object} pdf - The jsPDF instance
   * @param {Object} recipe - The recipe object
   * @param {Object} organizationBranding - Organization branding info
   * @param {number} margin - Page margin
   * @param {number} contentWidth - Content width
   * @param {number} pageWidth - Page width
   * @param {number} pageHeight - Page height
   * @param {number} startY - Starting Y position
   * @param {boolean} includeOrgHeader - Whether to include organization header
   * @returns {number} Final Y position
   */
  const renderRecipeLayout = (
    pdf,
    recipe,
    organizationBranding,
    margin,
    contentWidth,
    pageWidth,
    pageHeight,
    startY,
    includeOrgHeader = true,
  ) => {
    let yPosition = startY;

    // Reset font settings to defaults at the start
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);

    // Helper function to clean text - remove control characters and normalize
    const cleanText = (text) => {
      if (!text) return "";
      // Remove control characters (except newlines, tabs, and carriage returns)
      // Replace common problematic characters
      return String(text)
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // Remove control chars
        .replace(/\u200B/g, "") // Remove zero-width space
        .replace(/\u200C/g, "") // Remove zero-width non-joiner
        .replace(/\u200D/g, "") // Remove zero-width joiner
        .replace(/\uFEFF/g, "") // Remove zero-width no-break space
        .trim();
    };

    // Helper function to add text with word wrapping and better spacing
    const addWrappedText = (
      text,
      x,
      y,
      maxWidth,
      fontSize = 12,
      fontStyle = "normal",
      lineHeight = 1.4,
    ) => {
      // Clean text first to remove any control characters or encoding issues
      const cleanedText = cleanText(text);
      
      // CRITICAL: Set font size and style BEFORE splitting text
      // jsPDF's splitTextToSize uses current font settings to calculate width
      pdf.setFontSize(fontSize);
      pdf.setFont("helvetica", fontStyle);
      
      // Split text into lines based on maxWidth
      const lines = pdf.splitTextToSize(cleanedText, maxWidth);

      // Render each line with consistent font settings
      lines.forEach((line, index) => {
        // Ensure font is set for each line (in case it was changed)
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", fontStyle);
        pdf.text(line, x, y + index * fontSize * lineHeight * 0.35);
      });

      return y + lines.length * fontSize * lineHeight * 0.35 + 2; // Better spacing
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
    };

    // Add organization header if available and requested
    if (includeOrgHeader && organizationBranding?.name) {
      pdf.setTextColor(51, 51, 51);
      yPosition = addWrappedText(
        organizationBranding.name,
        margin,
        yPosition,
        contentWidth,
        16,
        "bold",
        1.2,
      );
      yPosition += 4;
    }

    // Add recipe title with better styling
    pdf.setFontSize(28);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    yPosition = addWrappedText(
      recipe.name || "Recipe",
      margin,
      yPosition,
      contentWidth,
      28,
      "bold",
      1.2,
    );
    yPosition += 0;

    // Add meal type chip with organization brand colors
    if (recipe.mealType) {
      // Get brand colors from organization branding - same as meal plan PDFs
      const getBrandColors = () => {
        const primary =
          organizationBranding?.brandingColors?.primary ||
          organizationBranding?.primary ||
          "#427674"; // Default Mighty45 teal
        const secondary =
          organizationBranding?.brandingColors?.secondary ||
          organizationBranding?.secondary ||
          "#2e7d32"; // Default green

        // Helper function to adjust color brightness
        const adjustColorBrightness = (hex, percent) => {
          if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex))
            return hex;

          const num = parseInt(hex.replace("#", ""), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = ((num >> 8) & 0x00ff) + amt;
          const B = (num & 0x0000ff) + amt;

          return (
            "#" +
            (
              0x1000000 +
              (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
              (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
              (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
              .toString(16)
              .slice(1)
          );
        };

        // Convert hex to RGB helper function
        const hexToRgb = (hex) => {
          if (!hex) return null;
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16),
              ]
            : null;
        };

        // Generate meal type colors using same logic as meal plan PDFs
        const mealTypeColors = {
          breakfast:
            organizationBranding?.brandingColors?.primaryLight ||
            organizationBranding?.primaryLight ||
            adjustColorBrightness(primary, 40),
          lunch:
            organizationBranding?.brandingColors?.secondary ||
            organizationBranding?.secondary ||
            secondary,
          dinner:
            organizationBranding?.brandingColors?.primary ||
            organizationBranding?.primary ||
            primary,
          snack:
            organizationBranding?.brandingColors?.secondaryLight ||
            organizationBranding?.secondaryLight ||
            adjustColorBrightness(secondary, 40),
        };

        // Convert to RGB for PDF usage
        const result = {
          primary: hexToRgb(primary) || [66, 118, 116],
          secondary: hexToRgb(secondary) || [46, 125, 50],
        };

        // Add meal type colors in RGB format
        Object.keys(mealTypeColors).forEach((mealType) => {
          result[mealType] =
            hexToRgb(mealTypeColors[mealType]) || result.primary;
        });

        return result;
      };

      const brandColors = getBrandColors();
      const color = brandColors[recipe.mealType] || brandColors.primary;
      pdf.setFillColor(...color);
      pdf.roundedRect(margin, yPosition - 10, 40, 12, 3, 3, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.text(recipe.mealType.toUpperCase(), margin + 3, yPosition - 3);
      yPosition += 10; // Proper spacing after meal type chip
    }

    // Add servings information if available
    if (recipe.servings && recipe.servings > 0) {
      pdf.setTextColor(75, 85, 99);
      const servingsText =
        recipe.servings === 1 ? "1 serving" : `${recipe.servings} servings`;
      yPosition = addWrappedText(
        `Makes ${servingsText}`,
        margin,
        yPosition,
        contentWidth,
        12,
        "bold",
        1.3,
      );
      yPosition += 4;
    }

    // Add description if available with better typography
    if (recipe.description) {
      // CRITICAL: Reset font to 12pt normal before wrapping
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(85, 85, 85);
      yPosition = addWrappedText(
        recipe.description,
        margin,
        yPosition,
        contentWidth,
        12,
        "normal",
        1.4,
      );
      yPosition += 8; // Reduced spacing after description
    }

    // Add nutrition information box with better design
    const recipeFiber =
      recipe.fiber ??
      recipe.macros?.fi ??
      recipe.mac?.fi ??
      recipe.mac?.fiber ??
      0;
    if (
      recipe.kCals ||
      recipe.proteins ||
      recipe.carbs ||
      recipe.fats ||
      recipeFiber
    ) {
      yPosition -= 15;
      checkPageBreak(50);

      // Nutrition title
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Nutrition Information", margin, yPosition + 12);

      // Nutrition values with better layout
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      let nutritionY = yPosition + 18;

      if (recipe.servings) {
        pdf.setTextColor(75, 85, 99);
        pdf.text(`Per serving`, margin, nutritionY);
        nutritionY += 8;
      }

      const nutritionItems = [
        {
          label: "Calories",
          value: recipe.kCals,
          unit: " kcal",
          color: [60, 60, 60], // Dark gray
        },
        {
          label: "Protein",
          value: recipe.proteins,
          unit: "g",
          color: [30, 60, 30], // Dark green-gray
        },
        {
          label: "Carbs",
          value: recipe.carbs,
          unit: "g",
          color: [60, 60, 30], // Dark yellow-gray
        },
        {
          label: "Fat",
          value: recipe.fats,
          unit: "g",
          color: [60, 30, 60], // Dark purple-gray
        },
        {
          label: "Fiber",
          value: recipeFiber,
          unit: "g",
          color: [30, 90, 40],
        },
      ];

      const visibleItems = nutritionItems.filter(
        (item) => item.value && item.value > 0,
      );
      const itemWidth =
        visibleItems.length > 0
          ? (contentWidth - 16) / visibleItems.length
          : 0;
      visibleItems.forEach((item, index) => {
        const xPos = margin + index * itemWidth;

        pdf.setTextColor(...item.color);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(`${Math.round(item.value)}${item.unit}`, xPos, nutritionY);

        pdf.setTextColor(107, 114, 128);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.text(item.label, xPos, nutritionY + 6);
      });

      yPosition += 30;
    }

    // Add divider line before ingredients if nutrition info was shown
    if (
      (recipe.kCals ||
        recipe.proteins ||
        recipe.carbs ||
        recipe.fats ||
        recipeFiber) &&
      recipe.ingredients &&
      recipe.ingredients.length > 0
    ) {
      yPosition += 8; // Space before divider
      pdf.setDrawColor(20, 20, 20);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10; // Space after divider
    }

    // Add ingredients section with better formatting
    if (recipe.ingredients && recipe.ingredients.length > 0) {
      yPosition += 0; // Add proper margin before ingredients
      checkPageBreak(20);

      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text("Ingredients", margin, yPosition);
      yPosition += 10;

      // Calculate column dimensions for two-column layout
      const columnWidth = (contentWidth - 12) / 2; // 12px gap between columns
      const leftColumnX = margin;
      const rightColumnX = margin + columnWidth + 12;

      // Split ingredients into two equal columns
      const ingredientsPerColumn = Math.ceil(recipe.ingredients.length / 2);
      const leftColumnIngredients = recipe.ingredients.slice(
        0,
        ingredientsPerColumn,
      );
      const rightColumnIngredients =
        recipe.ingredients.slice(ingredientsPerColumn);

      // Set up typography for ingredients
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");

      // Track Y positions for both columns
      let leftColumnY = yPosition;
      let rightColumnY = yPosition;

      // Render left column
      leftColumnIngredients.forEach((ingredient) => {
        checkPageBreak(12);
        // Create ingredient line with proper formatting and metric conversion
        const originalAmount = ingredient.amount;
        const amount = originalAmount ? decimalToFraction(originalAmount) : "";
        const unit =
          ingredient.unit && ingredient.unit !== "none" ? ingredient.unit : "";
        // Clean ingredient name - remove any prep time/duration info in parentheses that might cause overlap
        let name = ingredient.name || "";
        // Remove patterns like "(1 minute prep)", "(prep)", "(60 sec)", etc. from ingredient name
        name = name.replace(/\s*\([^)]*(?:prep|minute|min|sec|second|time)[^)]*\)/gi, "").trim();
        
        // Use original numeric amount for metric conversion, formatted amount for display
        const ingredientText = formatIngredientWithMetric(originalAmount, unit, name, amount);
        
        // Ensure font settings are correct
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(55, 65, 81);
        leftColumnY = addWrappedText(
          ingredientText,
          leftColumnX,
          leftColumnY,
          columnWidth,
          12,
          "normal",
          1.3,
        );
        leftColumnY += 2;
      });

      // Render right column
      rightColumnIngredients.forEach((ingredient) => {
        // Create ingredient line with proper formatting and metric conversion
        const originalAmount = ingredient.amount;
        const amount = originalAmount ? decimalToFraction(originalAmount) : "";
        const unit =
          ingredient.unit && ingredient.unit !== "none" ? ingredient.unit : "";
        // Clean ingredient name - remove any prep time/duration info in parentheses that might cause overlap
        let name = ingredient.name || "";
        // Remove patterns like "(1 minute prep)", "(prep)", "(60 sec)", etc. from ingredient name
        name = name.replace(/\s*\([^)]*(?:prep|minute|min|sec|second|time)[^)]*\)/gi, "").trim();
        
        // Use original numeric amount for metric conversion, formatted amount for display
        const ingredientText = formatIngredientWithMetric(originalAmount, unit, name, amount);
        
        // Ensure font settings are correct
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(55, 65, 81);
        rightColumnY = addWrappedText(
          ingredientText,
          rightColumnX,
          rightColumnY,
          columnWidth,
          12,
          "normal",
          1.3,
        );
        rightColumnY += 2;
      });

      // Set yPosition to the bottom of the longer column
      yPosition = Math.max(leftColumnY, rightColumnY) + 5;
    }

    // Add instructions section with better formatting
    if (recipe.instructions && recipe.instructions.length > 0) {
      yPosition += 0; // Add proper margin before instructions
      checkPageBreak(15);

      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text("Instructions", margin, yPosition);
      yPosition += 13;

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");

      recipe.instructions.forEach((step, index) => {
        checkPageBreak(20);

        // Step number with background circle using brand colors
        const stepNumberX = margin;
        const stepNumberY = yPosition - 2;
        const circleRadius = 4;
        const circleCenterX = stepNumberX + circleRadius;
        const circleCenterY = stepNumberY;

        // Use the same brand color logic as meal type chips
        const primary =
          organizationBranding?.brandingColors?.primary ||
          organizationBranding?.primary ||
          "#427674";
        const hexToRgb = (hex) => {
          if (!hex) return null;
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16),
              ]
            : null;
        };
        const primaryRgb = hexToRgb(primary) || [66, 118, 116];
        pdf.setFillColor(...primaryRgb);
        pdf.circle(circleCenterX, circleCenterY, circleRadius, "F");

        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        // Center the text in the circle
        const stepNumber = index + 1;
        const textWidth = pdf.getTextWidth(`${stepNumber}`);
        const textX = circleCenterX - textWidth / 2;
        const textY = circleCenterY + 1; // Adjust for font baseline to center vertically
        pdf.text(`${stepNumber}`, textX, textY);

        // Step instruction with proper text wrapping
        const instruction =
          typeof step === "string" ? step : step.instruction || "";
        let durationText = "";
        if (step.duration) {
          durationText =
            step.duration > 60
              ? `${Math.round(step.duration / 60)} min`
              : `${step.duration} sec`;
        }
        
        // Calculate available width for instruction text
        // Step circle center is at margin + radius(4), so right edge is at margin + 8
        // Add spacing between circle and text (8mm for better visual separation)
        const circleRightEdge = margin + circleRadius * 2; // margin + 8
        const instructionStartX = circleRightEdge + 8; // Add 8mm spacing after circle
        // Available width = full contentWidth minus the left offset
        const instructionWidth = contentWidth - (instructionStartX - margin);
        
        // CRITICAL: Reset font to 12pt normal before wrapping to ensure consistent sizing
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(55, 65, 81);
        
        // Use addWrappedText helper - it will set font again internally for consistency
        let instructionY = yPosition - 1;
        instructionY = addWrappedText(
          instruction,
          instructionStartX,
          instructionY,
          instructionWidth,
          12,
          "normal",
          1.4,
        );
        
        // Add duration on a new line after the instruction to avoid overlap
        if (durationText) {
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(120, 120, 120); // Medium gray
          pdf.setFontSize(10);
          pdf.text(`(${durationText})`, instructionStartX, instructionY);
          instructionY += 8; // Add spacing after duration
        }
        
        yPosition = instructionY + 5;
      });

      yPosition += 10;
    }

    return yPosition;
  };

  /**
   * Generate a beautifully formatted PDF from a recipe using jsPDF
   * @param {Object} recipe - The recipe object
   * @param {Object} organizationBranding - Organization branding info
   * @returns {Promise<void>}
   */
  const generateRecipePDF = async (recipe, organizationBranding = null) => {
    console.log("Starting PDF generation with:", {
      recipe: recipe?.name,
      organizationBranding: organizationBranding?.name,
    });
    setIsGenerating(true);

    try {
      // Create new PDF document
      const pdf = new jsPDF("p", "mm", "a4");
      console.log("PDF document created");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;

      // Use the shared layout function
      const finalY = renderRecipeLayout(
        pdf,
        recipe,
        organizationBranding,
        margin,
        contentWidth,
        pageWidth,
        pageHeight,
        margin,
        true, // Include organization header
      );

      // Add footer with better styling
      const footerY = pageHeight - 15;
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(156, 163, 175);

      // Add a subtle line above footer
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      pdf.line(margin, footerY - 8, pageWidth - margin, footerY - 8);

      pdf.text(`Created using the Mighty45.com Platform`, margin, footerY);

      // Open PDF in new tab for viewing/printing instead of downloading
      // jsPDF output("blob") already returns a blob with application/pdf type
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newTab = window.open(pdfUrl, "_blank");
      if (!newTab) {
        // Fallback if popup blocked - create download link
        const link = document.createElement("a");
        const pdfBlob = pdf.output("blob");
        link.href = URL.createObjectURL(pdfBlob);
        const cleanName = recipe.name
          ? recipe.name
              .replace(/[^a-z0-9\s]/gi, "")
              .replace(/\s+/g, "_")
              .toLowerCase()
          : "recipe";
        link.download = `${cleanName}_recipe.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        alert("Popup blocked. PDF has been downloaded instead.");
      }

      console.log("PDF opened in new tab!");
    } catch (error) {
      console.error("Error in PDF generation:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Generate PDF from HTML element using html2canvas
   * @param {string} elementId - ID of the HTML element to convert
   * @param {string} fileName - Name for the downloaded file
   * @returns {Promise<void>}
   */
  const generatePDFFromHTML = async (elementId, fileName = "recipe.pdf") => {
    setIsGenerating(true);

    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element with ID "${elementId}" not found`);
      }

      // Add comprehensive print styles for better PDF output
      const printStyles = document.createElement("style");
      printStyles.textContent = `
        #${elementId} {
          background: white !important;
          color: black !important;
          font-family: 'Helvetica', 'Arial', sans-serif !important;
          line-height: 1.4 !important;
          padding: 20px !important;
        }
        #${elementId} * {
          box-shadow: none !important;
          text-shadow: none !important;
        }
        /* Hide all interactive elements */
        #${elementId} .MuiButton-root,
        #${elementId} button,
        #${elementId} .MuiIconButton-root,
        #${elementId} [role="button"] {
          display: none !important;
        }
        /* Hide accordion arrows and expand icons */
        #${elementId} .MuiAccordionSummary-expandIconWrapper,
        #${elementId} .MuiSvgIcon-root[data-testid="ExpandMoreIcon"],
        #${elementId} .MuiAccordionSummary-content .MuiSvgIcon-root {
          display: none !important;
        }
        /* Make all accordions expanded and remove interactive styling */
        #${elementId} .MuiAccordion-root {
          box-shadow: none !important;
          border: 1px solid #e0e0e0 !important;
          margin: 8px 0 !important;
        }
        #${elementId} .MuiAccordionSummary-root {
          cursor: default !important;
          pointer-events: none !important;
          min-height: auto !important;
          padding: 12px 16px !important;
          background-color: #f5f5f5 !important;
        }
        #${elementId} .MuiAccordionDetails-root {
          display: block !important;
          padding: 16px !important;
        }
        /* Remove any hover states */
        #${elementId} .MuiAccordionSummary-root:hover {
          background-color: #f5f5f5 !important;
        }
        /* Ensure good spacing for print */
        #${elementId} .MuiTypography-h4,
        #${elementId} .MuiTypography-h5,
        #${elementId} .MuiTypography-h6 {
          page-break-after: avoid !important;
          margin-bottom: 8px !important;
        }
        #${elementId} .MuiList-root {
          padding: 0 !important;
        }
        #${elementId} .MuiListItem-root {
          padding: 4px 0 !important;
        }
        /* Remove any floating action buttons or overlays */
        #${elementId} .MuiFab-root,
        #${elementId} .MuiSpeedDial-root {
          display: none !important;
        }
      `;
      document.head.appendChild(printStyles);

      // Create canvas from HTML element with better quality settings
      const canvas = await html2canvas(element, {
        scale: 3, // Higher quality for crisp text
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        removeContainer: true,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          // Remove any interactive elements from the clone
          const buttons = clonedDoc.querySelectorAll(
            'button, .MuiButton-root, .MuiIconButton-root, [role="button"]',
          );
          buttons.forEach((btn) => btn.remove());

          // Expand all accordions by ensuring details are visible
          const accordionDetails = clonedDoc.querySelectorAll(
            ".MuiAccordionDetails-root",
          );
          accordionDetails.forEach((detail) => {
            detail.style.display = "block";
          });

          // Remove expand icons
          const expandIcons = clonedDoc.querySelectorAll(
            '.MuiAccordionSummary-expandIconWrapper, [data-testid="ExpandMoreIcon"]',
          );
          expandIcons.forEach((icon) => icon.remove());

          // Remove any floating action buttons
          const fabs = clonedDoc.querySelectorAll(
            ".MuiFab-root, .MuiSpeedDial-root",
          );
          fabs.forEach((fab) => fab.remove());
        },
      });

      // Remove temporary styles
      document.head.removeChild(printStyles);

      // Create PDF with better settings
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15; // Smaller margins for more content

      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      // Add image to PDF with better compression
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.95), // Use JPEG with high quality for smaller files
        "JPEG",
        margin,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
      );
      heightLeft -= pageHeight - 2 * margin;

      // Add new pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          margin,
          position,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
        heightLeft -= pageHeight - 2 * margin;
      }

      // Open PDF in new tab for viewing instead of downloading
      // jsPDF output("blob") already returns a blob with application/pdf type
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newTab = window.open(pdfUrl, "_blank");
      if (!newTab) {
        // Fallback if popup blocked
        const link = document.createElement("a");
        const pdfBlob = pdf.output("blob");
        link.href = URL.createObjectURL(pdfBlob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        alert("Popup blocked. PDF has been downloaded instead.");
      }
    } catch (error) {
      console.error("Error generating PDF from HTML:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Generate a calendar-style PDF for meal plan (summary view)
   * @param {Object} mealPlan - The meal plan object
   * @param {Array} recipes - Array of recipe objects
   * @param {Object} weeklySchedule - Weekly schedule data
   * @param {Object} organizationBranding - Organization branding info
   * @returns {Promise<void>}
   */
  const generateMealPlanCalendarPDF = async (
    mealPlan,
    recipes,
    weeklySchedule,
    organizationBranding = null,
  ) => {
    console.log("Starting meal plan calendar PDF generation...");
    setIsGenerating(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      // Helper function to add text with word wrapping
      const addWrappedText = (
        text,
        x,
        y,
        maxWidth,
        fontSize = 12,
        fontStyle = "normal",
        lineHeight = 1.4,
      ) => {
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", fontStyle);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, index) => {
          pdf.text(line, x, y + index * fontSize * lineHeight * 0.35);
        });
        return y + lines.length * fontSize * lineHeight * 0.35 + 2;
      };

      // Helper function to check page breaks
      const checkPageBreak = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
      };

      // Add organization header
      if (organizationBranding?.name) {
        pdf.setTextColor(51, 51, 51);
        yPosition = addWrappedText(
          organizationBranding.name,
          margin,
          yPosition,
          contentWidth,
          14,
          "bold",
          1.2,
        );
        yPosition += 4;
      }

      // Add meal plan title
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 0, 0);
      yPosition = addWrappedText(
        mealPlan.name || "Meal Plan",
        margin,
        yPosition,
        contentWidth,
        20,
        "bold",
        1.1,
      );
      yPosition += 3;

      // Add subtitle
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);
      pdf.text("Weekly Calendar View", margin, yPosition);
      yPosition += 10;

      // Add description if available
      if (mealPlan.description) {
        pdf.setTextColor(85, 85, 85);
        pdf.setFontSize(11);
        yPosition = addWrappedText(
          mealPlan.description,
          margin,
          yPosition,
          contentWidth,
          11,
          "normal",
          1.3,
        );
        yPosition += 3; // Reduced spacing
      }

      // Days of the week
      const daysOfWeek = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const dayLabels = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
      const mealTypeLabels = ["Breakfast", "Lunch", "Dinner", "Snack"];

      // Create recipe lookup for calendar PDF - handle both direct recipes and scheduled recipes with nested recipe property
      const recipeMap = {};
      recipes.forEach((recipe) => {
        // If this is a scheduled recipe with nested recipe data, extract it
        const actualRecipe = recipe.recipe || recipe;
        recipeMap[recipe.id] = actualRecipe;
      });

      // Get brand colors from organization branding - same as client theme
      const getBrandColors = () => {
        const primary =
          organizationBranding?.brandingColors?.primary ||
          organizationBranding?.primary ||
          "#427674"; // Default Mighty45 teal
        const secondary =
          organizationBranding?.brandingColors?.secondary ||
          organizationBranding?.secondary ||
          "#2e7d32"; // Default green

        // Helper function to adjust color brightness
        const adjustColorBrightness = (hex, percent) => {
          if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex))
            return hex;

          const num = parseInt(hex.replace("#", ""), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = ((num >> 8) & 0x00ff) + amt;
          const B = (num & 0x0000ff) + amt;

          return (
            "#" +
            (
              0x1000000 +
              (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
              (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
              (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
              .toString(16)
              .slice(1)
          );
        };

        // Helper function to adjust color hue
        const adjustColorHue = (hex, degrees) => {
          if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex))
            return hex;
          const hueShift = Math.abs(degrees) % 60;

          if (degrees > 0) {
            return adjustColorBrightness(hex, hueShift > 30 ? 20 : -10);
          } else {
            return adjustColorBrightness(hex, hueShift > 30 ? -20 : 10);
          }
        };

        // Convert hex to RGB helper function
        const hexToRgb = (hex) => {
          if (!hex) return null;
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16),
              ]
            : null;
        };

        // Generate meal type colors using same logic as ClientTheme
        const mealTypeColors = {
          breakfast:
            organizationBranding?.brandingColors?.primaryLight ||
            organizationBranding?.primaryLight ||
            adjustColorBrightness(primary, 40),
          lunch:
            organizationBranding?.brandingColors?.secondary ||
            organizationBranding?.secondary ||
            secondary,
          dinner:
            organizationBranding?.brandingColors?.primary ||
            organizationBranding?.primary ||
            primary,
          snack:
            organizationBranding?.brandingColors?.secondaryLight ||
            organizationBranding?.secondaryLight ||
            adjustColorBrightness(secondary, 40),
        };

        // Convert to RGB for PDF usage
        const result = {
          primary: hexToRgb(primary) || [66, 118, 116],
          secondary: hexToRgb(secondary) || [46, 125, 50],
        };

        // Add meal type colors in RGB format
        Object.keys(mealTypeColors).forEach((mealType) => {
          result[mealType] =
            hexToRgb(mealTypeColors[mealType]) || result.primary;
        });

        return result;
      };

      const brandColors = getBrandColors();

      // Generate calendar for each day
      daysOfWeek.forEach((day, dayIndex) => {
        checkPageBreak(60);

        // Day header with better styling using brand colors
        pdf.setFillColor(...brandColors.primary); // Use brand primary color
        pdf.roundedRect(margin, yPosition, contentWidth, 12, 2, 2, "F");

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(dayLabels[dayIndex], margin + 8, yPosition + 8);
        yPosition += 18;

        // Add margin between day title and first meal type
        yPosition += 3;

        // Get meals for this day - handle both data structures
        const dayMeals = weeklySchedule[day] || {};
        let dayHasMeals = false;

        mealTypes.forEach((mealType, mealIndex) => {
          let mealRecipes = [];

          // Handle both possible data structures
          if (dayMeals.mealTypes && dayMeals.mealTypes[mealType]) {
            // Processed structure with full recipe objects
            mealRecipes = dayMeals.mealTypes[mealType]
              .map((scheduledRecipe) => scheduledRecipe.recipe)
              .filter(Boolean);
          } else if (Array.isArray(dayMeals[mealType])) {
            // Simple structure with recipe IDs
            mealRecipes = dayMeals[mealType]
              .map((recipeId) => recipeMap[recipeId])
              .filter(Boolean);
          }

          if (mealRecipes.length > 0) {
            dayHasMeals = true;
            checkPageBreak(15 + mealRecipes.length * 5);

            // Meal type header with colored chip - more compact, no left margin
            const color = brandColors[mealType] || brandColors.primary;
            pdf.setFillColor(...color);
            pdf.roundedRect(margin, yPosition - 6, 45, 8, 2, 2, "F");

            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "bold");
            pdf.text(
              mealTypeLabels[mealIndex].toUpperCase(),
              margin + 3,
              yPosition - 1,
            );
            yPosition += 6;

            // Recipe names - no indents, more compact, no left margin
            mealRecipes.forEach((recipe, recipeIndex) => {
              if (recipe) {
                pdf.setTextColor(55, 65, 81);
                pdf.setFontSize(10);
                pdf.setFont("helvetica", "normal");

                const recipeName = recipe.name || `Recipe ${recipeIndex + 1}`;
                yPosition = addWrappedText(
                  recipeName,
                  margin,
                  yPosition,
                  contentWidth,
                  10,
                  "normal",
                  1.1,
                );
                yPosition += 1;
              }
            });
            yPosition += 4;
          }
        });

        // If no meals for this day, show a message
        if (!dayHasMeals) {
          pdf.setTextColor(156, 163, 175);
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "italic");
          pdf.text("No meals scheduled", margin, yPosition);
          yPosition += 8;
        }

        yPosition += 8; // Reduced space between days
      });

      // Add footer
      const footerY = pageHeight - 15;
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(156, 163, 175);
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      pdf.line(margin, footerY - 8, pageWidth - margin, footerY - 8);
      pdf.text(
        `Generated by ${organizationBranding?.name || "Mighty45"} • ${new Date().toLocaleDateString()}`,
        margin,
        footerY,
      );

      // Open PDF in new tab
      // jsPDF output("blob") already returns a blob with application/pdf type
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newTab = window.open(pdfUrl, "_blank");

      if (!newTab) {
        const link = document.createElement("a");
        const pdfBlob = pdf.output("blob");
        link.href = URL.createObjectURL(pdfBlob);
        const cleanName = mealPlan.name
          ? mealPlan.name
              .replace(/[^a-z0-9\s]/gi, "")
              .replace(/\s+/g, "_")
              .toLowerCase()
          : "meal_plan";
        link.download = `${cleanName}_calendar.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        alert("Popup blocked. PDF has been downloaded instead.");
      }
      // Clean up blob URL after delay
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      console.log("Meal plan calendar PDF opened in new tab!");
    } catch (error) {
      console.error("Error generating meal plan calendar PDF:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Generate a comprehensive PDF for meal plan with full recipe details
   * @param {Object} mealPlan - The meal plan object
   * @param {Array} recipes - Array of recipe objects
   * @param {Object} weeklySchedule - Weekly schedule data
   * @param {Object} organizationBranding - Organization branding info
   * @returns {Promise<void>}
   */
  const generateMealPlanFullPDF = async (
    mealPlan,
    recipes,
    weeklySchedule,
    organizationBranding = null,
  ) => {
    console.log("Starting full meal plan PDF generation...");
    console.log("Meal plan:", mealPlan);
    console.log("Recipes array:", recipes);
    console.log("Weekly schedule:", weeklySchedule);
    console.log("Organization branding:", organizationBranding);
    setIsGenerating(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      // Helper function to clean text - remove control characters and normalize
      const cleanText = (text) => {
        if (!text) return "";
        // Remove control characters (except newlines, tabs, and carriage returns)
        // Replace common problematic characters
        return String(text)
          .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // Remove control chars
          .replace(/\u200B/g, "") // Remove zero-width space
          .replace(/\u200C/g, "") // Remove zero-width non-joiner
          .replace(/\u200D/g, "") // Remove zero-width joiner
          .replace(/\uFEFF/g, "") // Remove zero-width no-break space
          .trim();
      };

      // Helper functions (same as recipe PDF)
      const addWrappedText = (
        text,
        x,
        y,
        maxWidth,
        fontSize = 12,
        fontStyle = "normal",
        lineHeight = 1.4,
      ) => {
        // Clean text first to remove any control characters or encoding issues
        const cleanedText = cleanText(text);
        
        // CRITICAL: Set font size and style BEFORE splitting text
        // jsPDF's splitTextToSize uses current font settings to calculate width
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", fontStyle);
        
        // Split text into lines based on maxWidth
        const lines = pdf.splitTextToSize(cleanedText, maxWidth);

        // Render each line with consistent font settings
        lines.forEach((line, index) => {
          // Ensure font is set for each line (in case it was changed)
          pdf.setFontSize(fontSize);
          pdf.setFont("helvetica", fontStyle);
          pdf.text(line, x, y + index * fontSize * lineHeight * 0.35);
        });

        return y + lines.length * fontSize * lineHeight * 0.35 + 2;
      };

      const checkPageBreak = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
      };

      const addFooter = () => {
        const footerY = pageHeight - 15;
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(156, 163, 175);
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.5);
        pdf.line(margin, footerY - 8, pageWidth - margin, footerY - 8);
        pdf.text(
          `Generated by ${organizationBranding?.name || "Mighty45"} • ${new Date().toLocaleDateString()}`,
          margin,
          footerY,
        );
      };

      // Add organization header
      if (organizationBranding?.name) {
        pdf.setTextColor(51, 51, 51);
        yPosition = addWrappedText(
          organizationBranding.name,
          margin,
          yPosition,
          contentWidth,
          16,
          "bold",
          1.2,
        );
        yPosition += 4;
      }

      // Add meal plan title
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 0, 0);
      yPosition = addWrappedText(
        mealPlan.name || "Meal Plan",
        margin,
        yPosition,
        contentWidth,
        20,
        "bold",
        1.1,
      );
      yPosition += 3;

      // Add subtitle
      pdf.setTextColor(100, 100, 100);
      yPosition = addWrappedText(
        "Complete Meal Plan with Recipe Details",
        margin,
        yPosition,
        contentWidth,
        12,
        "normal",
        1.2,
      );
      yPosition += 6;

      // Add description if available
      if (mealPlan.description) {
        pdf.setTextColor(85, 85, 85);
        pdf.setFontSize(11);
        yPosition = addWrappedText(
          mealPlan.description,
          margin,
          yPosition,
          contentWidth,
          11,
          "normal",
          1.3,
        );
        yPosition += 3; // Reduced spacing
      }

      // First, add the weekly overview
      const daysOfWeek = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const dayLabels = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
      const mealTypeLabels = ["Breakfast", "Lunch", "Dinner", "Snack"];

      // Create recipe lookup for full PDF - handle both direct recipes and scheduled recipes with nested recipe property
      const recipeMap = {};
      recipes.forEach((recipe) => {
        // If this is a scheduled recipe with nested recipe data, extract it
        const actualRecipe = recipe.recipe || recipe;
        recipeMap[recipe.id] = actualRecipe;
      });

      // Get brand colors from organization branding - same as client theme
      const getBrandColors = () => {
        const primary =
          organizationBranding?.brandingColors?.primary ||
          organizationBranding?.primary ||
          "#427674"; // Default Mighty45 teal
        const secondary =
          organizationBranding?.brandingColors?.secondary ||
          organizationBranding?.secondary ||
          "#2e7d32"; // Default green

        // Helper function to adjust color brightness
        const adjustColorBrightness = (hex, percent) => {
          if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex))
            return hex;

          const num = parseInt(hex.replace("#", ""), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = ((num >> 8) & 0x00ff) + amt;
          const B = (num & 0x0000ff) + amt;

          return (
            "#" +
            (
              0x1000000 +
              (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
              (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
              (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
              .toString(16)
              .slice(1)
          );
        };

        // Convert hex to RGB helper function
        const hexToRgb = (hex) => {
          if (!hex) return null;
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16),
              ]
            : null;
        };

        // Generate meal type colors using same logic as ClientTheme
        const mealTypeColors = {
          breakfast:
            organizationBranding?.brandingColors?.primaryLight ||
            organizationBranding?.primaryLight ||
            adjustColorBrightness(primary, 40),
          lunch:
            organizationBranding?.brandingColors?.secondary ||
            organizationBranding?.secondary ||
            secondary,
          dinner:
            organizationBranding?.brandingColors?.primary ||
            organizationBranding?.primary ||
            primary,
          snack:
            organizationBranding?.brandingColors?.secondaryLight ||
            organizationBranding?.secondaryLight ||
            adjustColorBrightness(secondary, 40),
        };

        // Convert to RGB for PDF usage
        const result = {
          primary: hexToRgb(primary) || [66, 118, 116],
          secondary: hexToRgb(secondary) || [46, 125, 50],
        };

        // Add meal type colors in RGB format
        Object.keys(mealTypeColors).forEach((mealType) => {
          result[mealType] =
            hexToRgb(mealTypeColors[mealType]) || result.primary;
        });

        return result;
      };

      const brandColors = getBrandColors();

      // Weekly Overview Section
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Weekly Overview", margin, yPosition);
      yPosition += 12;

      // Add compact weekly schedule
      daysOfWeek.forEach((day, dayIndex) => {
        checkPageBreak(20);

        pdf.setFillColor(248, 249, 250);
        pdf.rect(margin, yPosition - 3, contentWidth, 15, "F");

        pdf.setTextColor(51, 51, 51);
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        pdf.text(dayLabels[dayIndex], margin + 5, yPosition + 3);

        const dayMeals = weeklySchedule[day] || {};
        let mealText = "";

        mealTypes.forEach((mealType, mealIndex) => {
          let mealRecipes = [];

          // Handle both possible data structures
          if (dayMeals.mealTypes && dayMeals.mealTypes[mealType]) {
            // Processed structure with full recipe objects
            mealRecipes = dayMeals.mealTypes[mealType]
              .map((scheduledRecipe) => scheduledRecipe.recipe)
              .filter(Boolean);
          } else if (Array.isArray(dayMeals[mealType])) {
            // Simple structure with recipe IDs
            mealRecipes = dayMeals[mealType]
              .map((recipeId) => recipeMap[recipeId])
              .filter(Boolean);
          }

          if (mealRecipes.length > 0) {
            const recipeNames = mealRecipes
              .map((recipe) => recipe?.name || "Unknown")
              .join(", ");
            mealText += `${mealTypeLabels[mealIndex]}: ${recipeNames}; `;
          }
        });

        if (mealText) {
          pdf.setTextColor(85, 85, 85);
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "normal");
          yPosition = addWrappedText(
            mealText.slice(0, -2),
            margin + 50,
            yPosition,
            contentWidth - 50,
            9,
            "normal",
            1.2,
          );
        } else {
          pdf.setTextColor(156, 163, 175);
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "italic");
          pdf.text("No meals scheduled", margin + 50, yPosition + 3);
          yPosition += 6;
        }

        yPosition += 8;
      });

      // Start new page for recipes
      pdf.addPage();
      yPosition = margin;

      // Extract actual recipe objects from the scheduled recipes
      // Handle both direct recipes (client view) and scheduled recipes with nested recipe property (coach view)
      const actualRecipes = recipes.map((scheduledRecipe) => {
        // If this has a nested recipe property, use that; otherwise use the object itself
        return scheduledRecipe.recipe || scheduledRecipe;
      });

      const uniqueRecipes = [...new Set(actualRecipes.map((r) => r.id))].map(
        (id) => actualRecipes.find((r) => r.id === id),
      );

      console.log("Unique recipes found:", uniqueRecipes);
      console.log("Number of unique recipes:", uniqueRecipes.length);

      uniqueRecipes.forEach((recipe, index) => {
        console.log(`Processing recipe ${index + 1}:`, recipe);
        if (index > 0) {
          pdf.addPage();
        }

        // Reset font settings before rendering each recipe
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(0, 0, 0);

        // Use the shared layout function for each recipe
        yPosition = renderRecipeLayout(
          pdf,
          recipe,
          organizationBranding,
          margin,
          contentWidth,
          pageWidth,
          pageHeight,
          margin, // Starting Y position for each recipe page
          false, // Don't include organization header on individual recipe pages
        );

        // Add footer to each page
        addFooter();
      });

      // Open PDF in new tab
      // jsPDF output("blob") already returns a blob with application/pdf type
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newTab = window.open(pdfUrl, "_blank");

      if (!newTab) {
        const link = document.createElement("a");
        const pdfBlob = pdf.output("blob");
        link.href = URL.createObjectURL(pdfBlob);
        const cleanName = mealPlan.name
          ? mealPlan.name
              .replace(/[^a-z0-9\s]/gi, "")
              .replace(/\s+/g, "_")
              .toLowerCase()
          : "meal_plan";
        link.download = `${cleanName}_full.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        alert("Popup blocked. PDF has been downloaded instead.");
      }
      console.log("Full meal plan PDF opened in new tab!");
    } catch (error) {
      console.error("Error generating full meal plan PDF:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateRecipePDF,
    generatePDFFromHTML,
    generateMealPlanCalendarPDF,
    generateMealPlanFullPDF,
    isGenerating,
  };
};

export default usePDFGenerator;
